import React from 'react'
import { FieldErrors, SubmitHandler, SubmitErrorHandler } from 'react-hook-form'
import get from 'lodash/get'
import moment from 'moment'
import ControlledSelect from 'transfix-form/components/ControlledSelect'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import { Button } from 'transfix-ui/components/Button'
import { Box, Grid, Typography } from 'transfix-ui/core'
import { shadows } from 'transfix-ui/theme/shadows'
import { ValueField } from 'apps/carrier/components/Compliance'
import { IStep1 } from 'apps/fleet-planner/components/activation/types'
import { useActivationContext } from 'apps/fleet-planner/context'
import { useActivationPageHighwayQuery } from 'apps/fleet-planner/pages/activation/graphql/ActivationPageHighway'
import { getHighwayCarrierInfo } from 'apps/fleet-planner/utils/getHighwayCarrierInfo'
import { Separator } from 'apps/marketplace/pages/facilities/components/Separator'
import FORMATS from 'constants/datetime'
import { carrierEntities } from 'constants/entities'
import { TCompanyInformationPreFilledSchema } from './CompanyInformationPreFilledSchema'
import { useCompanyInformationFormPreFilled } from './useCompanyInformationFormPreFilled'

const getErrorMessage = <
  T extends FieldErrors<TCompanyInformationPreFilledSchema>,
  K extends keyof T
>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

export interface ICompanyInformationFormPreFilledProps {
  defaultValues?: Partial<TCompanyInformationPreFilledSchema>
  onSubmitSuccess: SubmitHandler<IStep1>
  onSubmitError?: SubmitErrorHandler<TCompanyInformationPreFilledSchema>
}

const NO_DATA_LABEL = 'No data available'

export const CompanyInformationFormPreFilled = ({
  defaultValues = {},
  onSubmitSuccess,
  onSubmitError = () => {},
}: ICompanyInformationFormPreFilledProps) => {
  const { control, errors, handleSubmit } = useCompanyInformationFormPreFilled({
    defaultValues,
  })
  const { carrierDetails } = useActivationContext()
  const { fleetPlannerMotorCarrierId = '' } = carrierDetails || {}

  const { data, loading } = useActivationPageHighwayQuery({
    variables: { motorCarrierId: fleetPlannerMotorCarrierId },
  })

  const highWayCarrierInfo = getHighwayCarrierInfo(data)
  const { address, city, continuousAuthorityGrantDate, country, state, zipcode } =
    highWayCarrierInfo || {}

  const submitHandler = handleSubmit(onSubmitSuccess, onSubmitError)

  return (
    <form onSubmit={submitHandler} data-test='CompanyInformationFormPreFilled'>
      <Grid item xs={12}>
        <Box mb={2}>
          <Typography variant='subtitle2'>Verify Entity Information</Typography>
        </Box>
        <Box mb={2}>
          <ControlledSelect
            control={control}
            name='entityType'
            SelectProps={{
              label: 'Entity Type',
              options: carrierEntities,
              validationMessage: getErrorMessage(errors, 'entityType'),
              error: !!getErrorMessage(errors, 'entityType'),
              large: true,
              placeholder: 'Select Entity Type',
              dataTest: 'input-entity-type',
              required: true,
            }}
          />
        </Box>
        <Box mb={4}>
          <ControlledTextField
            name='ein'
            control={control}
            TextInputProps={{
              type: 'number',
              label: 'EIN',
              validationMessage: getErrorMessage(errors, 'ein'),
              error: !!getErrorMessage(errors, 'ein'),
              placeholder: 'Enter Employee Identification Number',
              dataTest: 'ein-input',
              required: true,
              large: true,
            }}
          />
        </Box>

        <Box mb={2}>
          <Typography variant='subtitle2'>Review Company Information</Typography>
        </Box>
        <Box mb={3} boxShadow={shadows[4]} borderRadius='8px' p={2}>
          <Box display='grid' gap='16px'>
            <ValueField
              title='In Operation Since'
              titleColor='textPrimary'
              value={
                continuousAuthorityGrantDate &&
                moment(continuousAuthorityGrantDate).format(FORMATS.dateFormat)
              }
              fallback={NO_DATA_LABEL}
              loading={loading}
            />
            <Separator />
            <ValueField
              title='Country'
              titleColor='textPrimary'
              value={country}
              fallback={NO_DATA_LABEL}
              loading={loading}
            />
            <ValueField
              title='Address'
              titleColor='textPrimary'
              value={address}
              fallback={NO_DATA_LABEL}
              loading={loading}
            />
            <ValueField
              title='City'
              titleColor='textPrimary'
              value={city}
              fallback={NO_DATA_LABEL}
              loading={loading}
            />
            <ValueField
              title='State'
              titleColor='textPrimary'
              value={state}
              fallback={NO_DATA_LABEL}
              loading={loading}
            />
            <ValueField
              title='ZIP Code'
              titleColor='textPrimary'
              value={zipcode}
              fallback={NO_DATA_LABEL}
              loading={loading}
            />
          </Box>
        </Box>
        <Box>
          <Grid container justifyContent='space-between' alignItems='flex-end'>
            <Typography variant='body2'>
              If this information is incorrect,{' '}
              <a href='mailto:carrier-app-support@transfix.io'>contact support</a>
            </Typography>
            <Grid item xs={12} md={4}>
              <Button type='submit' color='primary' data-test='submit-button' fullWidth>
                Yes, Continue
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </form>
  )
}
